import {
  type FC,
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider, type UseFormReturn, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { isValid } from 'date-fns';
import { z } from 'zod';
import {
  DateTimeGranularity,
  getDateTimeInputConfig,
  validateDateInputString,
  validateDateTimeInputString,
  validateDateTimeIsoString,
} from '@/utilities/date';
import { type Asset, type AssetWorkflowMetadata, assetWorkflowMetadataSchema } from '@/types/asset';
import { type BackendError, useFormErrorHandler } from '@/hooks/useFormErrorHandler';
import { type MetadataField, type PublicMetadataField } from '@/types/metadata';
import { useBatchEditAssets } from '@/hooks/data/assets/useBatchEditAssets';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useUploadStore } from '@/hooks/data/inspector/useUploadStore';
import { SortOrderOptions, SortOrderValues } from '@/types/sort';
import { saveAssetMetadata } from '@/services/metadata.service';
import { useMetadataList } from '@/hooks/metadata/useMetadata';
import { useCapabilities } from '@/hooks/useCapabilities';
import { ObjectAction, ObjectType } from '@/types/batch';
import { useTreeContext } from '@/context/TreeContext';
import { useToast } from '@/components/ui/use-toast';
import { getUploadCsvKey } from '@/lib/utils';
import { QueryKey } from '@/lib/queryKeys';
import { log } from '@/utilities/log';
import { useTranslations } from 'next-intl';

/**
 * Owns the Metadata panel's data and its single form.
 *
 * The save bar lives at the bottom of the Inspector panel rather than inside the metadata section, so
 * that one click saves everything the panel holds — today the metadata fields, tomorrow whatever else
 * is added beside them. That only works if the form outlives any one section, which is what this
 * provider is for: sections render fields into it and the bar reads its dirty state and submits it.
 *
 * Keeping the bar out of the accordion also means the section no longer has to stretch to the panel's
 * bottom edge, no longer needs `overflow-visible` to let a sticky element escape Radix's clipping, and
 * so keeps its open/close animation.
 */
/** The subset of a real MetadataField a submission link can safely expose to a contributor —
 * no tenant/audit data, but the real `options`, unlike PublicMetadataField's value-derived ones. */
export type ContributorMetadataDefinition = Pick<MetadataField, 'id' | 'slug' | 'name' | 'type' | 'options'>;

export type MetadataPanelFormProps = {
  asset?: Asset;
  multipleAssetsMetadata?: AssetWorkflowMetadata;
  // True while the aggregation behind `multipleAssetsMetadata` is still loading. A guest has no private
  // metadata list to wait on, so this is the only signal that its fields are on their way.
  multipleAssetsMetadataIsLoading?: boolean;
  selectedItems?: Array<{ id: string; name: string; originalName?: string | null }>;
  folderId?: string;
  albumId?: string;
  isUpload?: boolean;
  enabled: boolean;
  /** A contributor's guest identity can't call the tenant metadata-list endpoint `enabled` gates;
   * this supplies field definitions from the resolved submission link instead. */
  contributorMetadataDefinitions?: Array<ContributorMetadataDefinition>;
};

type MetadataPanelFormValue = {
  editedField: string | null;
  registerFieldRow: (slug: string, element: HTMLElement | null) => void;
  getEditedFieldRow: () => HTMLElement | null;
  form: UseFormReturn<Record<string, any>>;
  metadata: Array<MetadataField | PublicMetadataField>;
  metadataListIsFetching: boolean;
  canViewPrivateMetadata: boolean;
  formEdited: boolean;
  valuesVersion: number;
  isLoading: boolean;
  markEdited: (slug: string) => void;
  submit: () => void;
  revert: () => void;
};

const MetadataPanelFormContext = createContext<MetadataPanelFormValue | null>(null);

export const useMetadataPanelForm = (): MetadataPanelFormValue => {
  const context = useContext(MetadataPanelFormContext);

  if (!context) {
    throw new Error('useMetadataPanelForm must be used within a MetadataPanelFormProvider');
  }

  return context;
};

export const MetadataPanelFormProvider: FC<PropsWithChildren<MetadataPanelFormProps>> = ({
  children,
  asset,
  multipleAssetsMetadata,
  multipleAssetsMetadataIsLoading,
  selectedItems,
  folderId,
  albumId,
  isUpload,
  enabled,
  contributorMetadataDefinitions,
}) => {
  const tToasts = useTranslations('toasts');

  const { toast } = useToast();
  const { canViewPrivateMetadata } = useCapabilities();
  const { batchEditAssetsMutation } = useBatchEditAssets();
  const { includeSubFolderAssets } = useTreeContext();
  const { getActiveUploadSession, setManualMetadata } = useUploadStore();
  const { uploadSessionId, session } = getActiveUploadSession();
  const { csvData, manualMetadata } = session;

  const [formEdited, setFormEdited] = useState(false);
  const didEditRef = useRef(false);
  // Which field is being edited, and the row element of each field. A row holds the label, the control
  // and its validation message, so measuring one covers everything that has to stay visible. State
  // rather than a ref for the slug because consumers re-measure when it changes; React bails out when
  // it is unchanged, so typing in one field does not re-render.
  const [editedField, setEditedField] = useState<string | null>(null);
  const fieldRowsRef = useRef(new Map<string, HTMLElement>());
  // Bumped after every form reset; the fields are keyed on it so they remount once the reset has
  // already landed. See the reset effect for why a remount is needed at all.
  const [valuesVersion, setValuesVersion] = useState(0);

  const queryClient = useQueryClient();

  const metadataValues = useMemo(() => {
    if (isUpload && selectedItems && selectedItems.length > 0) {
      setFormEdited(false);
      didEditRef.current = false;

      const allMetadataObjects = selectedItems.map(({ id, name, originalName }) => {
        const csvKey = getUploadCsvKey({ name, originalName });
        const csv = (csvData[csvKey]?.metadata ?? {}) as Record<string, any>;

        // Find the manual metadata using the id
        const manual = (manualMetadata[id]?.metadata ?? {}) as Record<string, any>;

        return { ...csv, ...manual };
      });

      const first = allMetadataObjects[0] ?? {};
      const final: Record<string, any> = {};

      for (const key of Object.keys(first)) {
        if (key === 'csvDataKeyRecognition') {
          continue;
        }

        const value = first[key];

        const isSameAcrossAll = allMetadataObjects.every((obj) => key in obj && obj[key] === value);

        if (isSameAcrossAll) {
          final[key] = value;
        }
      }

      return final;
    }
    if (multipleAssetsMetadata) {
      return multipleAssetsMetadata;
    } else if (asset?.metadata) {
      return asset.metadata;
    }
    return {};
  }, [asset?.metadata, csvData, isUpload, manualMetadata, multipleAssetsMetadata, selectedItems]);

  const { data: privateMetadata, isFetching: metadataListIsFetching } = useMetadataList(
    {
      pagination: {
        offset: null,
        limit: null,
      },
      sort: {
        value: SortOrderValues.MANUAL,
        order: SortOrderOptions.ASC,
      },
      queryString: '',
    },
    {
      enabled: enabled,
      // Keep data “fresh” for 5 minutes (300 000 ms)
      staleTime: 5 * 60_000,
      // On mount, only refetch if data is stale
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchInterval: false,
    },
  );

  const metadataIsFetching = metadataListIsFetching || Boolean(multipleAssetsMetadataIsLoading);

  /** Placeholder audit fields: confirmed nothing in this panel's rendering reads anything
   * beyond id/slug/name/type/options/description. */
  const contributorMetadata = useMemo<Array<MetadataField> | undefined>(() => {
    if (!contributorMetadataDefinitions) {
      return undefined;
    }
    return contributorMetadataDefinitions.map((definition) => ({
      ...definition,
      tenantId: '',
      searchable: false,
      facet: false,
      public: false,
      createdBy: { id: '', name: '' },
      createdAt: '',
      updatedBy: { id: '', name: '' },
      updatedAt: '',
    }));
  }, [contributorMetadataDefinitions]);

  const metadata = useMemo(() => {
    if (contributorMetadata) {
      return contributorMetadata;
    }
    return canViewPrivateMetadata
      ? privateMetadata
      : (Object.values(metadataValues as unknown as Array<PublicMetadataField>) ?? []);
  }, [canViewPrivateMetadata, metadataValues, privateMetadata, contributorMetadata]);

  const validationSchema = useMemo(() => {
    const fields = metadata ?? [];

    const schema = (assetWorkflowMetadataSchema ?? z.record(z.string(), z.any())) as z.ZodType<
      Record<string, any>,
      any
    >;

    return schema.superRefine((values, ctx) => {
      if (!values || Array.isArray(values)) {
        return;
      }

      fields.forEach((field) => {
        if (field.type !== 'date' && field.type !== 'datetime') {
          return;
        }

        const value = (values as Record<string, unknown>)[field.slug];
        if (value === null || value === undefined || value === '') {
          return;
        }

        const granularity = field.type === 'date' ? DateTimeGranularity.Day : DateTimeGranularity.Second;
        const placeholder = getDateTimeInputConfig(granularity).placeholder;

        if (value instanceof Date) {
          if (!isValid(value)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Invalid date format. Use ${placeholder}.`,
              path: [field.slug],
            });
          }
          return;
        }

        if (typeof value === 'string') {
          const validationResult =
            field.type === 'date'
              ? validateDateInputString(value)
              : value.includes('T')
                ? validateDateTimeIsoString(value)
                : validateDateTimeInputString(value);

          if (!validationResult.valid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: validationResult.message || `Invalid date format. Use ${placeholder}.`,
              path: [field.slug],
            });
          }
          return;
        }

        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid date format. Use ${placeholder}.`,
          path: [field.slug],
        });
      });
    });
  }, [metadata]);

  const publicMetadataValues = useMemo(() => {
    return (
      !canViewPrivateMetadata &&
      // Guest metadata is a record keyed by slug for a single asset but an array for a
      // multi-select; key off each field's own slug either way rather than the container key/index.
      Object.fromEntries(
        Object.values(
          metadataValues as unknown as Record<string, PublicMetadataField> | Array<PublicMetadataField>,
        ).map((field) => [field.slug, field.value]),
      )
    );
  }, [canViewPrivateMetadata, metadataValues]);

  const form = useForm({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: canViewPrivateMetadata ? metadataValues : (publicMetadataValues as Record<string, any>),
  });

  // Reset form values when metadataValues changes (e.g., when switching assets)
  useEffect(() => {
    const newValues = canViewPrivateMetadata ? metadataValues : (publicMetadataValues as Record<string, any>);

    // A key simply MISSING from the reset payload does not clear: RHF re-populates a still-registered
    // Controller field from its current value, so `reset({})` leaves the old string in form state
    // (verified with getValues() straight after). Absence is exactly how a multi-asset aggregation
    // reports "these assets no longer agree", so every key currently held is named explicitly as
    // undefined and only then overlaid with the new values (DM-2524).
    const clearedValues = Object.fromEntries(Object.keys(form.getValues()).map((key) => [key, undefined]));

    form.reset({ ...clearedValues, ...newValues });

    initialValuesRef.current = newValues;

    didEditRef.current = false;
    setFormEdited(false);

    // Remount the fields, but only now that the reset has landed. A reset that CLEARS a field does not
    // reach RHF's `Controller`: form state loses the value while the field goes on rendering the old
    // one (`setValue(name, undefined)` does not reach it either), and a remount is the only thing that
    // makes it re-read. That matters because a cleared field is how a multi-asset aggregation reports
    // "these assets no longer agree", which has to show as indeterminate (DM-2524 review). Keying the
    // fields on the data instead would remount them during the render BEFORE this effect runs, while
    // form state still holds the stale value. Still required as of react-hook-form 7.85.
    setValuesVersion((version) => version + 1);
  }, [metadataValues, publicMetadataValues, canViewPrivateMetadata, form]);

  const { handleError } = useFormErrorHandler(form.setError, form.getValues);

  const saveAssetMetadataWithAuth = useAuthenticatedQueryFn(saveAssetMetadata);

  const initialValuesRef = useRef(
    canViewPrivateMetadata
      ? (metadata as Array<MetadataField>).reduce(
          (acc: Record<string, any>, field: MetadataField) => {
            const key = field.slug;
            acc[key] = form.getValues()[key] ?? '';
            return acc;
          },
          {} as Record<string, any>,
        )
      : form.getValues(),
  );

  const saveAssetMetadataMutation = useMutation({
    mutationFn: saveAssetMetadataWithAuth,
    onSuccess: () => {
      initialValuesRef.current = form.getValues();

      didEditRef.current = false;
      setFormEdited(false);

      toast({
        title: tToasts('saveAssetMetadataMutation.title'),
        description: tToasts('saveAssetMetadataMutation.description'),
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.Asset] });

      // Editing one asset changes what a multi-asset selection agrees on, and those aggregations live
      // under their own query key, so they would otherwise survive this write and report a field as
      // shared when the assets now differ (DM-2524 review). Invalidating is not enough: the app sets
      // `refetchOnMount: false` (pages/_app.tsx) and `invalidateQueries` only refetches ACTIVE
      // queries, so an unmounted aggregation would still be served stale the next time that selection
      // is made. Dropping the entries forces a fresh fetch instead.
      queryClient.removeQueries({ queryKey: [QueryKey.MultipleAssets] });
    },
    onError: (err: BackendError) => {
      handleError(err);
      log(err);
    },
  });

  function onSubmit(values: z.infer<typeof assetWorkflowMetadataSchema>) {
    if (!values) {
      return;
    }
    const changedMetadata = Object.keys(form.formState.dirtyFields).reduce(
      (acc, key) => {
        acc[key] = values[key];
        return acc;
      },
      {} as Record<string, any>,
    );
    if (isUpload && selectedItems && selectedItems.length > 0) {
      const filteredValues = values
        ? Object.fromEntries(Object.entries(values).filter(([_, value]) => value !== '' || value !== undefined))
        : {};

      setManualMetadata(uploadSessionId, { metadata: filteredValues }, selectedItems, true);
      initialValuesRef.current = form.getValues();
      didEditRef.current = false;
      setFormEdited(false);
    } else if (multipleAssetsMetadata) {
      batchEditAssetsMutation.mutate({
        objectType: ObjectType.Asset,
        objectAction: ObjectAction.Update,
        filter: {
          ...(selectedItems && { assetIds: selectedItems.map((selectedAsset) => selectedAsset.id) }),
          ...(folderId && { folderId, includeSubFolderAssets }),
          ...(albumId && { albumId }),
        },
        payload: {
          metadata: changedMetadata,
        },
      });
    } else {
      saveAssetMetadataMutation.mutate({
        asset_id: asset?.id,
        metadata: changedMetadata,
      });
    }
  }

  const isLoading = saveAssetMetadataMutation.isPending || batchEditAssetsMutation.isPending;
  const registerFieldRow = useCallback((slug: string, element: HTMLElement | null) => {
    if (element) {
      fieldRowsRef.current.set(slug, element);
    } else {
      fieldRowsRef.current.delete(slug);
    }
  }, []);

  const getEditedFieldRow = useCallback(
    () => (editedField ? (fieldRowsRef.current.get(editedField) ?? null) : null),
    [editedField],
  );

  const markEdited = useCallback((slug: string) => {
    setEditedField(slug);

    if (!didEditRef.current) {
      didEditRef.current = true;
      // schedule the state update after render
      setTimeout(() => setFormEdited(true), 0);
    }
  }, []);

  // `onSubmit` is redeclared each render, so it cannot be a dependency here without recreating
  // `submit` (and re-rendering the save bar) on every keystroke. The handler is invoked, never
  // compared, so reading the latest one through a ref keeps this stable.
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const submit = useCallback(() => {
    void form.handleSubmit((values) => onSubmitRef.current(values))();
  }, [form]);

  const revert = useCallback(() => {
    form.reset(initialValuesRef.current);
    didEditRef.current = false;
    setFormEdited(false);
  }, [form]);

  const value = useMemo(
    () => ({
      form,
      editedField,
      registerFieldRow,
      getEditedFieldRow,
      metadata: (metadata ?? []) as Array<MetadataField | PublicMetadataField>,
      metadataListIsFetching: metadataIsFetching,
      canViewPrivateMetadata,
      formEdited,
      valuesVersion,
      isLoading,
      markEdited,
      submit,
      revert,
    }),
    [
      form,
      editedField,
      registerFieldRow,
      getEditedFieldRow,
      metadata,
      metadataIsFetching,
      canViewPrivateMetadata,
      formEdited,
      valuesVersion,
      isLoading,
      markEdited,
      submit,
      revert,
    ],
  );

  return (
    <MetadataPanelFormContext.Provider value={value}>
      <FormProvider {...form}>{children}</FormProvider>
    </MetadataPanelFormContext.Provider>
  );
};
